/**
 * useSimilarTickets Hook
 *
 * Manages similar tickets state and operations
 */

import { useState, useCallback } from 'react';
import { findSimilarTickets, type SimilarTicket } from '../services/api';

interface UseSimilarTicketsReturn {
  tickets: SimilarTicket[];
  loading: boolean;
  error: string | null;
  findSimilar: (ticketId: number, keywords?: string[]) => Promise<void>;
  clear: () => void;
}

export function useSimilarTickets(): UseSimilarTicketsReturn {
  const [tickets, setTickets] = useState<SimilarTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Find similar tickets
   */
  const findSimilar = useCallback(async (ticketId: number, keywords?: string[]) => {
    setLoading(true);
    setError(null);

    try {
      const result = await findSimilarTickets(ticketId, keywords);
      setTickets(result.tickets);
    } catch (err: any) {
      console.error('[useSimilarTickets] Error:', err.message);
      setError(err.message || 'Failed to find similar tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear results
   */
  const clear = useCallback(() => {
    setTickets([]);
    setError(null);
  }, []);

  return {
    tickets,
    loading,
    error,
    findSimilar,
    clear,
  };
}
